// src/components/SearchPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import vietnam from "../data/vietnam.json";

export default function SearchPanel({ initial = {} }) {
  const [params] = useSearchParams();
  const nav = useNavigate();

  // init từ URL / prop
  const qInit = params.get("q") || initial.q || "";
  const provInit = params.get("province") || initial.province || "";
  const distInit = params.get("district") || initial.district || "";
  const wardInit = params.get("ward") || initial.ward || "";

  const [q, setQ] = useState(qInit);
  const [province, setProvince] = useState(provInit);
  const [district, setDistrict] = useState(distInit);
  const [ward, setWard] = useState(wardInit);

  // ====== Gợi ý dịch vụ (nguồn dữ liệu) ======
  const [svcNames, setSvcNames] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/services");
        const data = await r.json();
        const items = Array.isArray(data) ? data : (data.items || []);
        const names = [...new Set(
          items.map(s => (s.name ?? s.title ?? "").toString().trim()).filter(Boolean)
        )].sort((a, b) => a.localeCompare(b, 'vi'));
        if (!alive) return;
        setSvcNames(names);
      } catch {/* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  // ====== Dropdown gợi ý cho ô tìm kiếm ======
  const boxRef = useRef(null);
  const [openList, setOpenList] = useState(false);
  const [hilite, setHilite] = useState(-1);

  const norm = (v = "") => v.toString().toLowerCase();
  const filtered = useMemo(() => {
    const t = norm(q);
    const arr = !t ? svcNames : svcNames.filter(n => norm(n).includes(t));
    return arr.slice(0, 30);
  }, [q, svcNames]);

  useEffect(() => {
    const onClick = (e) => {
      if (!boxRef.current) return;
      if (openList && !boxRef.current.contains(e.target)) {
        setOpenList(false); setHilite(-1);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") { setOpenList(false); setHilite(-1); } };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [openList]);

  function choose(val) {
    setQ(val);
    setOpenList(false);
    setHilite(-1);
  }

  function onInputKey(e) {
    if (!openList && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpenList(true);
      return;
    }
    if (!openList) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHilite(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHilite(h => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (hilite >= 0 && hilite < filtered.length) {
        e.preventDefault();
        choose(filtered[hilite]);
      }
    }
  }

  // ====== Danh sách Tỉnh / Huyện / Xã ======
  const provinces = useMemo(() => {
    const set = new Set();
    for (const r of vietnam) set.add((r.province || "").trim());
    return [...set].filter(Boolean).sort();
  }, []);

  const districts = useMemo(() => {
    const set = new Set();
    for (const r of vietnam) {
      const p = (r.province || "").trim();
      const d = (r.district || "").trim();
      if (!d) continue;
      if (!province || p === province) set.add(d);
    }
    return [...set].filter(Boolean).sort();
  }, [province]);

  const wards = useMemo(() => {
    const set = new Set();
    for (const r of vietnam) {
      const p = (r.province || "").trim();
      const d = (r.district || "").trim();
      const w = (r.ward || "").trim();
      if (!w) continue;
      if (district) {
        if (d === district) set.add(w);
      } else if (province) {
        if (p === province) set.add(w);
      } else {
        set.add(w);
      }
    }
    return [...set].filter(Boolean).sort();
  }, [province, district]);

  // ====== Popover khu vực ======
  const [open, setOpen] = useState(false);
  const [applied, setApplied] = useState(!!(provInit || distInit || wardInit));
  const popRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onClick = (e) => {
      if (!popRef.current) return;
      if (open && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  // label nút khu vực: ưu tiên Xã -> Huyện -> Tỉnh
  const areaLabel = ward || district || province || "Chọn khu vực";

  function applyLocation(e) {
    e && e.preventDefault && e.preventDefault();
    const qp = new URLSearchParams();
    if (q.trim()) qp.set("q", q.trim());
    if (province) qp.set("province", province);
    if (district) qp.set("district", district);
    if (ward) qp.set("ward", ward);
    nav(qp.toString() ? `/?${qp.toString()}` : "/");
    setApplied(!!(province || district || ward));
    setOpen(false);
  }

  function submitQuick(e) {
    e.preventDefault();
    applyLocation();
  }

  return (
    <div className="bg-white rounded-2xl shadow p-3 md:p-4">
      {/* MOBILE-FIRST: search full width; hàng dưới có nút khu vực + Tìm.
          Desktop: 1 hàng gọn. */}
      <form onSubmit={submitQuick} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3">
        {/* Search combobox */}
        <div className="md:col-span-8 relative" ref={boxRef}>
          <div className="relative">
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpenList(true); setHilite(-1); }}
              onFocus={() => setOpenList(true)}
              onKeyDown={onInputKey}
              placeholder="Tìm dịch vụ..."
              className="w-full h-11 md:h-12 rounded-xl border px-3 pr-9 outline-none"
              autoComplete="off"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={openList}
              aria-controls="svc-listbox"
              aria-haspopup="listbox"
            />
            {/* nút toggle dropdown */}
            <button
              type="button"
              onClick={() => setOpenList(v => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg text-slate-500 hover:bg-slate-100"
              aria-label="Gợi ý dịch vụ"
            >
              ▼
            </button>
          </div>

          {/* dropdown list */}
          {openList && filtered.length > 0 && (
            <ul
              id="svc-listbox"
              role="listbox"
              className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-xl border bg-white shadow"
            >
              {filtered.map((name, i) => (
                <li
                  key={name + i}
                  role="option"
                  aria-selected={hilite === i}
                  onMouseDown={(e) => e.preventDefault()} // tránh blur input trước click
                  onClick={() => choose(name)}
                  className={
                    "px-3 py-2 cursor-pointer text-sm " +
                    (hilite === i ? "bg-rose-50 text-rose-700" : "hover:bg-slate-50")
                  }
                >
                  {name}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Hàng dưới trên mobile: khu vực + tìm */}
        <div className="flex gap-2 md:col-span-4">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={
              "flex-1 h-11 md:h-12 px-4 rounded-xl border flex items-center justify-center gap-2 " +
              (applied
                ? "bg-rose-50 border-rose-300 text-rose-700 hover:bg-rose-100"
                : "bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100")
            }
            aria-expanded={open}
            aria-haspopup="dialog"
            title="Chọn khu vực"
          >
            <span>📍</span>
            <span className="font-medium truncate">{areaLabel}</span>
          </button>

          <button
            type="submit"
            className="h-11 md:h-12 px-5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold"
          >
            Tìm
          </button>
        </div>
      </form>

      {/* Popover Khu vực */}
      {open && (
        <>
          <div className="fixed inset-0 bg-black/10 backdrop-blur-[1px] z-40"></div>

          <div
            ref={popRef}
            role="dialog"
            aria-label="Chọn khu vực"
            className="z-50 fixed left-1/2 top-28 -translate-x-1/2 w-[92vw] max-w-[460px] rounded-2xl bg-white shadow-xl ring-1 ring-slate-100"
          >
            <div className="px-5 pt-4 pb-2 border-b">
              <div className="text-center font-semibold text-lg">Khu vực</div>
            </div>

            <form onSubmit={applyLocation} className="p-5 grid grid-cols-1 gap-3">
              <div>
                <label className="text-sm text-slate-600 block mb-1">Chọn tỉnh thành</label>
                <select
                  value={province}
                  onChange={(e) => { setProvince(e.target.value); setDistrict(""); setWard(""); }}
                  className="w-full h-11 rounded-xl border px-3"
                >
                  <option value="">-- Không chọn --</option>
                  {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <div>
                <label className="text-sm text-slate-600 block mb-1">Chọn quận huyện (tùy chọn)</label>
                <select
                  value={district}
                  onChange={(e) => { setDistrict(e.target.value); setWard(""); }}
                  className="w-full h-11 rounded-xl border px-3"
                >
                  <option value="">-- Không chọn --</option>
                  {districts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <div>
                <label className="text-sm text-slate-600 block mb-1">Chọn xã/phường (tùy chọn)</label>
                <select
                  value={ward}
                  onChange={(e) => setWard(e.target.value)}
                  className="w-full h-11 rounded-xl border px-3"
                >
                  <option value="">-- Không chọn --</option>
                  {wards.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>

              <div className="pt-1">
                <button
                  type="submit"
                  className="w-full h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold"
                >
                  Áp dụng
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
